import { Monitor } from 'lucide-react';
import { EDITOR_DESKTOP_MEDIA_QUERY } from '@/components/app-editor/use-editor-chrome';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { CreateWebsiteBannerDto } from '@/generated/korza-api-client/types.gen';
import { useMediaQuery } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  formatExtraHostnames,
  parseExtraHostnames,
} from '@/lib/website-banner-constants';
import { LazyMonaco } from './lazy-monaco';

export interface AdvancedPanelProps {
  form: CreateWebsiteBannerDto;
  setForm: React.Dispatch<React.SetStateAction<CreateWebsiteBannerDto>>;
  updateCustomStyles: (
    patch: Partial<CreateWebsiteBannerDto['customStyles']>
  ) => void;
}

/**
 * CSS editors only from 1024px (AD-030); below, a card says so. The saved CSS
 * (iframeStyle included) stays in the form either way. Extra hostnames are not
 * code and are editable at every size (D6).
 */
export function AdvancedPanel({
  form,
  setForm,
  updateCustomStyles,
}: AdvancedPanelProps) {
  const { LL } = useI18nContext();
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);

  return (
    <div className="space-y-4">
      {isDesktop ? (
        <>
          <div className="space-y-2">
            <Label>{LL.websiteBannerPage.advanced.bannerCssLabel()}</Label>
            <LazyMonaco
              language="css"
              value={form.customStyles.bannerCustomCss}
              onChange={(v) => updateCustomStyles({ bannerCustomCss: v })}
              minHeight="100px"
            />
            <p className="text-sm text-muted-foreground">
              {LL.websiteBannerPage.advanced.bannerCssDescription()}
            </p>
          </div>
          <div className="space-y-2">
            <Label>{LL.websiteBannerPage.advanced.pageCssLabel()}</Label>
            <LazyMonaco
              language="css"
              value={form.customStyles.pageCustomCss}
              onChange={(v) => updateCustomStyles({ pageCustomCss: v })}
              minHeight="80px"
            />
            <p className="text-sm text-muted-foreground">
              {LL.websiteBannerPage.advanced.pageCssDescription()}
            </p>
          </div>
        </>
      ) : (
        <div className="flex gap-3 rounded-lg border bg-muted/40 p-4">
          <Monitor
            aria-hidden
            className="mt-0.5 size-5 shrink-0 text-muted-foreground"
          />
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {LL.websiteBannerEditor.advanced.desktopOnlyTitle()}
            </p>
            <p className="text-sm text-muted-foreground">
              {LL.websiteBannerEditor.advanced.desktopOnlyDescription()}
            </p>
          </div>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="banner-extra-hostnames">
          {LL.websiteBannerPage.advanced.extraHostnamesLabel()}
        </Label>
        <Textarea
          id="banner-extra-hostnames"
          className="min-h-[80px] max-md:text-base"
          value={formatExtraHostnames(form.extraHostnames)}
          onChange={(e) => {
            const extraHostnames = parseExtraHostnames(e.target.value);
            setForm((prev) => ({ ...prev, extraHostnames }));
          }}
          placeholder={LL.websiteBannerPage.advanced.extraHostnamesPlaceholder()}
        />
        <p className="text-sm text-muted-foreground">
          {LL.websiteBannerPage.advanced.extraHostnamesDescription()}
        </p>
      </div>
    </div>
  );
}
